<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import ContactAPI from '../../../api/contacts';
import KanbanAPI from '../../../api/kanban';

const props = defineProps({
  attributeKey: { type: String, default: '' },
  attributeValue: { type: String, default: '' },
  prefilledContactId: { type: Number, default: null },
  prefilledContactName: { type: String, default: '' },
  prefilledConversationId: { type: Number, default: null },
});

const emit = defineEmits(['created', 'close']);

const { t } = useI18n();

// Attribute defs (non-CRM only), filtered by funnel if set
const contactAttributeDefs = useMapGetter('attributes/getContactAttributes');
const cardAttributeDefs = computed(() =>
  (contactAttributeDefs.value || []).filter(
    attr =>
      attr.attributeDisplayType !== 'crm' &&
      attr.attributeKey !== 'kbw_card_color' &&
      (!attr.funnelKeys?.length || attr.funnelKeys.includes(props.attributeKey))
  )
);

// Pipeline selection
const pipelines = ref([]);
const selectedAttributeKey = ref(props.attributeKey || '');
const selectedAttributeValue = ref(props.attributeValue || '');
const pipelineColumns = computed(() => {
  if (!selectedAttributeKey.value) return [];
  const pipeline = pipelines.value.find(
    p => p.attribute_key === selectedAttributeKey.value
  );
  return pipeline?.attribute_values || [];
});

onMounted(async () => {
  if (!props.attributeKey) {
    try {
      const res = await KanbanAPI.get({});
      pipelines.value = res.data?.listAttributes || [];
      if (pipelines.value.length > 0) {
        selectedAttributeKey.value = pipelines.value[0].attribute_key;
      }
    } catch {
      pipelines.value = [];
    }
  }
});

watch(selectedAttributeKey, () => {
  selectedAttributeValue.value = '';
});

const isNumericAttr = def =>
  def.attributeDisplayType === 'currency' ||
  def.attributeDisplayType === 'number';

// Contact search
const contactSearch = ref(props.prefilledContactName || '');
const contactResults = ref([]);
const selectedContact = ref(
  props.prefilledContactId
    ? { id: props.prefilledContactId, name: props.prefilledContactName }
    : null
);
const searchingContacts = ref(false);
const isPrefilledContact = !!props.prefilledContactId;

// Card fields
const color = ref('#3b82f6');
const cardAttrs = ref({});
const errorMsg = ref('');

// Initialize cardAttrs when defs load
watch(
  cardAttributeDefs,
  defs => {
    defs.forEach(def => {
      if (!(def.attributeKey in cardAttrs.value)) {
        cardAttrs.value[def.attributeKey] = '';
      }
    });
  },
  { immediate: true }
);

let searchTimer = null;
const searchContacts = async query => {
  if (!query || query.length < 2) {
    contactResults.value = [];
    return;
  }
  searchingContacts.value = true;
  try {
    const res = await ContactAPI.search(query, 1);
    contactResults.value = res.data?.payload || [];
  } catch {
    contactResults.value = [];
  } finally {
    searchingContacts.value = false;
  }
};

watch(contactSearch, val => {
  if (isPrefilledContact) return;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => searchContacts(val), 300);
});

const selectContact = contact => {
  selectedContact.value = contact;
  contactSearch.value = contact.name;
  contactResults.value = [];
};

const submit = () => {
  errorMsg.value = '';
  if (!selectedContact.value) {
    errorMsg.value = t('KANBAN.CREATE_CARD.ERROR_CONTACT');
    return;
  }
  if (!selectedAttributeKey.value) {
    errorMsg.value = t('KANBAN.CREATE_CARD.ERROR_PIPELINE');
    return;
  }

  const additionalAttributes = Object.fromEntries(
    Object.entries(cardAttrs.value)
      .filter(([, v]) => v !== '' && v !== null && v !== undefined)
      .map(([k, v]) => [k, v])
  );

  const payload = {
    contact_id: selectedContact.value.id,
    attribute_key: selectedAttributeKey.value,
    attribute_value: selectedAttributeValue.value || '',
    color: color.value,
    additional_attributes: additionalAttributes,
  };

  if (props.prefilledConversationId) {
    payload.conversation_id = props.prefilledConversationId;
  }

  emit('created', payload);
  emit('close');
};
</script>

<template>
  <div
    class="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col"
      @click.stop
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-600 shrink-0"
      >
        <h2 class="text-base font-semibold text-slate-900 dark:text-slate-100">
          {{ $t('KANBAN.CREATE_CARD.TITLE') }}
        </h2>
        <button
          class="p-1 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          @click="$emit('close')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="px-6 py-4 space-y-4 overflow-y-auto flex-1">
        <!-- Pipeline selector -->
        <div v-if="!attributeKey">
          <label
            class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ $t('KANBAN.CREATE_CARD.PIPELINE_LABEL') }}
          </label>
          <select
            v-model="selectedAttributeKey"
            class="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
          >
            <option
              v-for="p in pipelines"
              :key="p.attribute_key"
              :value="p.attribute_key"
            >
              {{ p.attribute_display_name }}
            </option>
          </select>
        </div>

        <!-- Stage selector -->
        <div v-if="!attributeValue && pipelineColumns.length > 0">
          <label
            class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ $t('KANBAN.CREATE_CARD.STAGE_LABEL') }}
          </label>
          <select
            v-model="selectedAttributeValue"
            class="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
          >
            <option value="">
              {{ $t('KANBAN.CREATE_CARD.STAGE_PLACEHOLDER') }}
            </option>
            <option v-for="col in pipelineColumns" :key="col" :value="col">
              {{ col }}
            </option>
          </select>
        </div>

        <!-- Contact -->
        <div>
          <label
            class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ $t('KANBAN.CREATE_CARD.CONTACT_LABEL') }}
          </label>
          <div
            v-if="isPrefilledContact"
            class="px-3 py-2 bg-slate-50 dark:bg-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300"
          >
            {{ prefilledContactName }}
          </div>
          <div v-else class="relative">
            <input
              v-model="contactSearch"
              type="text"
              :placeholder="$t('KANBAN.CREATE_CARD.CONTACT_PLACEHOLDER')"
              class="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
            />
            <div
              v-if="contactResults.length > 0"
              class="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              <button
                v-for="contact in contactResults"
                :key="contact.id"
                class="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                @click="selectContact(contact)"
              >
                <span class="font-medium">{{ contact.name }}</span>
                <span
                  v-if="contact.phone_number"
                  class="text-slate-400 ml-2 text-xs"
                  >{{ contact.phone_number }}</span
                >
              </button>
            </div>
            <div
              v-if="searchingContacts"
              class="absolute right-3 top-2.5 text-slate-400"
            >
              <woot-spinner size="small" />
            </div>
          </div>
        </div>

        <!-- Dynamic card attributes -->
        <template v-if="cardAttributeDefs.length">
          <div class="border-t border-slate-100 dark:border-slate-700 pt-4">
            <p
              class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3"
            >
              {{ $t('KANBAN.CREATE_CARD.ATTRIBUTES_LABEL') }}
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="def in cardAttributeDefs"
                :key="def.attributeKey"
                class="flex flex-col gap-1"
                :class="
                  def.attributeKey === 'observacoes' ||
                  def.attributeDisplayType === 'text'
                    ? 'col-span-2'
                    : ''
                "
              >
                <label class="text-xs text-slate-500 dark:text-slate-400">{{
                  def.attributeDisplayName
                }}</label>

                <select
                  v-if="def.attributeDisplayType === 'list'"
                  v-model="cardAttrs[def.attributeKey]"
                  class="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-n-brand"
                >
                  <option value="">
                    {{ $t('KANBAN.CONTACT_MODAL.SELECT_PLACEHOLDER') }}
                  </option>
                  <option
                    v-for="opt in def.attributeValues"
                    :key="opt"
                    :value="opt"
                  >
                    {{ opt }}
                  </option>
                </select>

                <input
                  v-else-if="def.attributeDisplayType === 'date'"
                  v-model="cardAttrs[def.attributeKey]"
                  type="date"
                  class="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />

                <input
                  v-else-if="isNumericAttr(def)"
                  v-model="cardAttrs[def.attributeKey]"
                  type="number"
                  step="0.01"
                  min="0"
                  class="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />

                <textarea
                  v-else-if="def.attributeKey === 'observacoes'"
                  v-model="cardAttrs[def.attributeKey]"
                  rows="2"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-n-brand resize-none"
                />

                <input
                  v-else
                  v-model="cardAttrs[def.attributeKey]"
                  type="text"
                  class="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- Color -->
        <div>
          <label
            class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ $t('KANBAN.CREATE_CARD.COLOR_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model="color"
              type="color"
              class="w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer"
            />
            <span class="text-sm text-slate-500 dark:text-slate-400">{{
              color
            }}</span>
          </div>
        </div>

        <p v-if="errorMsg" class="text-sm text-red-600 dark:text-red-400">
          {{ errorMsg }}
        </p>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-slate-200 dark:border-slate-600 flex justify-end gap-3 shrink-0"
      >
        <button
          class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          @click="$emit('close')"
        >
          {{ $t('KANBAN.CREATE_CARD.CANCEL') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium bg-n-brand text-white rounded-lg hover:bg-n-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!selectedContact || !selectedAttributeKey"
          @click="submit"
        >
          {{ $t('KANBAN.CREATE_CARD.SUBMIT') }}
        </button>
      </div>
    </div>
  </div>
</template>
